import { Button, Grid, Typography, withStyles } from "@material-ui/core";
import { Trans, useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import { BannerAlert } from "features/shared/components";
import React, { useEffect, useState } from "react";
import { DATE_FORMATS, DATE_UNIT, DateUtils } from "shared/dateUtils";

const ArrobaBalanceHeader = ({ classes, dates, getBalanceData }) => {
  const { t } = useTranslations();

  const weighingDateOptions = dates.map((item) => ({
    label: DateUtils.parse(item.DataPesagem).format(DATE_FORMATS["DD/MM/YYYY"]),
    value: DateUtils.parse(item.DataPesagem).format(DATE_FORMATS["DD/MM/YYYY"]),
  }));

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [formError, setFormError] = useState({
    hasDateError: false,
    dateErrorMessage: null,
  });

  useEffect(() => {
    const optionsCount = weighingDateOptions.length;

    const lastWeighingDate =
      optionsCount >= 1 ? weighingDateOptions[optionsCount - 1].value : null;
    const lastButOneWeighingDate =
      optionsCount >= 2 ? weighingDateOptions[optionsCount - 2].value : null;
    setStartDate(lastButOneWeighingDate);
    setEndDate(lastWeighingDate);
  }, [dates]);

  useEffect(() => {
    const startDateLabel = (
      weighingDateOptions.find((dp) => dp.value === startDate) || {}
    ).label;
    const endDateLabel = (
      weighingDateOptions.find((dp) => dp.value === endDate) || {}
    ).label;

    const parsedStartDate = DateUtils.parse(
      startDateLabel,
      DATE_FORMATS["DD-MM-YYYY"]
    );
    const parsedEndDate = DateUtils.parse(
      endDateLabel,
      DATE_FORMATS["DD-MM-YYYY"]
    );

    if (parsedEndDate.isSame(parsedStartDate, DATE_UNIT.DAY)) {
      setFormError({
        hasDateError: true,
        dateErrorMessage: t("arrobaBalance:same-date-selected-error"),
      });
      return;
    }

    if (parsedStartDate.isAfter(parsedEndDate, DATE_UNIT.DAY)) {
      setFormError({
        hasDateError: true,
        dateErrorMessage: t("common:validation.start-date-gt-end-date"),
      });

      return;
    }

    setFormError({ hasDateError: false, dateErrorMessage: null });
  }, [startDate, endDate, dates]);

  const handleGenerateBalance = () => {
    getBalanceData(startDate, endDate);
  };

  const handleChangeStartDate = (value) => {
    setStartDate(value);
  };

  const handleChangeEndDate = (value) => {
    setEndDate(value);
  };

  return (
    <Grid>
      <Typography className={classes.pageTitle}>
        {t("arrobaBalance:arroba-balance")}
      </Typography>
      <BannerAlert
        className={classes.disclaimer}
        data-testid="conciliation-disclaimer"
        type="warning"
      >
        <Trans
          i18nKey="arrobaBalance:keep-herd-reconciled-disclaimer"
          components={{ bold: <strong /> }}
        />
      </BannerAlert>
      {formError.hasDateError && (
        <Typography
          data-testid="balance-form-error"
          className={classes.errorMessage}
        >
          {formError.dateErrorMessage}
        </Typography>
      )}
      <div className={classes.container}>
        <Grid item xs={3}>
          <SelectDropdown
            testId="start-date-select"
            label={{ text: t("common:fields.start-date") }}
            options={weighingDateOptions}
            onChange={handleChangeStartDate}
            value={startDate}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectDropdown
            testId="end-date-select"
            label={{ text: t("common:fields.end-date") }}
            options={weighingDateOptions}
            value={endDate}
            onChange={handleChangeEndDate}
          />
        </Grid>
        <Grid item xs={5}>
          <Button
            data-testid="generate-balance-button"
            variant="contained"
            color="primary"
            className={classes.generateButton}
            disabled={formError.hasDateError}
            onClick={handleGenerateBalance}
          >
            {t("arrobaBalance:generate-balance")}
          </Button>
        </Grid>
      </div>
    </Grid>
  );
};

const styles = () => ({
  pageTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    fontStyle: "medium",
    fontStretch: "medium",
    lineHeight: "medium",
    letterSpacing: "medium",
    color: "#333333",
    marginTop: "40px",
    marginBottom: "20px",
  },
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  disclaimer: {
    marginBottom: "50px",
    marginTop: "40px",
  },
  errorMessage: {
    color: "#ff0102",
    fontFamily: "Roboto",
    marginLeft: 2,
    fontSize: "12px",
  },
  generateButton: {
    height: "36px",
    width: "180px",
    borderRadius: "18px",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontWeight: "500",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "1.25px",
    textAlign: "center",
    color: "#ffffff",
  },
});

export default withStyles(styles)(ArrobaBalanceHeader);
